import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useContactModal } from '../contexts/ContactModalContext';

const Footer = () => {
  const [contact, setContact] = useState<any>({
    phone: '+91 9567810944',
    email: 'masterhomedevelopers@gmail.com',
    address: 'SM COMPLEX, MAIN ROAD, near IRITTY TALUK CO-OP SOCIETY, 19TH MILE, KANNUR, Kerala 670702'
  });
  const [social, setSocial] = useState<any>({});
  const [workingHours] = useState({
    weekdays: {
      days: 'Monday - Saturday',
      hours: '9:00 AM - 6:00 PM'
    },
    sunday: {
      days: 'Sunday',
      hours: 'Closed'
    }
  });
  const currentYear = new Date().getFullYear();
  const { openModal } = useContactModal();

  useEffect(() => {
    const fetchContent = async () => {
      const { data: contentData } = await supabase
        .from('site_content')
        .select('*')
        .in('section', ['footer', 'settings']);

      if (contentData) {
        const footerContact = contentData.find(item => item.section === 'footer' && item.key === 'contact');
        const footerSocial = contentData.find(item => item.section === 'footer' && item.key === 'social');
        const hoursContent = contentData.find(item => item.section === 'settings' && item.key === 'working_hours');

        if (footerContact) setContact(footerContact.value);
        if (footerSocial) setSocial(footerSocial.value);
        if (hoursContent) setWorkingHours(hoursContent.value);
      }
    };

    fetchContent();
  }, []);

  return (
    <footer className="bg-gray-900 pt-16 pb-12">
      <div className="container-padding">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <img 
                src="./logo.png" 
                alt="Master Home Logo" 
                className="h-10 w-auto brightness-0 invert" 
              />
              <div className="text-white">
                <h1 className="text-xl font-bold font-display">Master Home</h1>
              </div>
            </Link>
            <p className="text-gray-400 mb-6">
              Building your dreams into reality, one home at a time.
            </p>
            <div className="flex space-x-4">
              {Object.entries(social).map(([platform, url]) => {
                const Icon = {
                  facebook: Facebook,
                  twitter: Twitter,
                  instagram: Instagram,
                  linkedin: Linkedin
                } as Record<string, React.ComponentType<any>>;

                return Icon && (
                  <a
                    key={platform}
                    href={url as string}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-6">Contact</h4>
            <div className="space-y-4">
              <a 
                href="tel:+919567810944" 
                className="flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <Phone className="h-5 w-5 mr-3" />
                +91 9567810944
              </a>
              <a 
                href="mailto:masterhomedevelopers@gmail.com" 
                className="flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <Mail className="h-5 w-5 mr-3" />
                masterhomedevelopers@gmail.com
              </a>
              <div className="space-y-4">
                <h3 className="text-white font-semibold">Visit Us</h3>
                <a
                  href="https://maps.google.com/?q=SM+COMPLEX,+MAIN+ROAD,+near+IRITTY+TALUK+CO-OP+SOCIETY,+19TH+MILE,+KANNUR,+Kerala+670702"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-gray-400 hover:text-red-500 transition-colors group"
                >
                  <MapPin className="h-5 w-5 mr-3 mt-1 flex-shrink-0 group-hover:text-red-500" />
                  <span>
                    SM COMPLEX, MAIN ROAD,<br />
                    near IRITTY TALUK CO-OP SOCIETY,<br />
                    19TH MILE, KANNUR,<br />
                    Kerala 670702
                  </span>
                </a>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { label: 'About Us', path: '/about' },
                { label: 'Our Services', path: '/services' },
                { label: 'Learning Center', path: '/learning-center' },
                { label: 'Careers', path: '/careers' },
                { label: 'Contact', onClick: openModal },
              ].map(({ label, path, onClick }) => (
                <li key={label}>
                  {onClick ? (
                    <a
                      onClick={onClick}
                      className="group flex items-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <ArrowRight className="h-4 w-4 mr-2 transition-transform group-hover:translate-x-1" />
                      {label}
                    </a>
                  ) : (
                    <Link
                      to={path!}
                      className="group flex items-center text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <ArrowRight className="h-4 w-4 mr-2 transition-transform group-hover:translate-x-1" />
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-6">Working Hours</h4>
            <ul className="space-y-4 text-gray-400">
              <li>
                <span className="block font-medium text-white">Monday - Saturday</span>
                9:00 AM - 6:00 PM
              </li>
              <li>
                <span className="block font-medium text-white">Sunday</span>
                Closed
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">
              © {currentYear} Master Home. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <a href="#" className="hover:text-red-500 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-red-500 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-red-500 transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
